import React, { useState, useCallback, useMemo } from 'react'
import {
  makeErrorNotification,
  notifyOk,
  notifyWarning
} from '@@partner:helpers/notification'
import { mergeLeft } from 'merge-left-utils'
import { useFetchOrder } from '@@partner:graphql/query/order/order.gql'
import { usePlaceOrder } from '@@partner:graphql/mutation/order/placeOrder.gql'

import { Redirect } from 'react-router-dom'
import { Checkbox, Divider, Message, Panel, Tooltip, Whisper } from 'rsuite'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import OrderForm from './orderForm/OrderForm'

import { CartContextProvider } from './cart/CartContext'
import { ProductSearchContextProvider } from './product/search/ProductSearchContext'

import { ORDER_STATUS_INITIAL } from '@@partner:config/order-status-codes'
import { ROUTE_ORDERS } from '@@partner:config/routes'

interface OrderSettings {
  forcePlaceOrder: boolean
}

const DEFAULT_ORDER_SETTINGS: OrderSettings = {
  forcePlaceOrder: false
}

export interface EditOrderFormProps {
  orderId: number
}

export default function EditOrderForm ({ orderId }: EditOrderFormProps) {
  const [ isSent, setIsSent ] = useState<boolean>(false)
  const [ redirectToOrders, setRedirectToOrders ] = useState<boolean>(false)
  const [ orderSettings, setOrderSettings ] = useState<OrderSettings>(
    DEFAULT_ORDER_SETTINGS
  )

  const [ placeOrder, { loading: loadingPlaceOrder } ] = usePlaceOrder()

  const { data, loading, error, refetch } = useFetchOrder(orderId)

  const order = data?.order

  const isOrderPlaceable = useMemo(
    () =>
      order?.status === ORDER_STATUS_INITIAL || orderSettings.forcePlaceOrder,
    [ order?.status, orderSettings.forcePlaceOrder ]
  )

  const handleFinish = useCallback(
    (wantToPlaceOrder: boolean) => {
      // we wanna place our order
      if (order) {
        if (wantToPlaceOrder && isOrderPlaceable) {
          placeOrder({
            variables: {
              order_id: order.id,
              isUpdatePlacing: orderSettings.forcePlaceOrder
            }
          })
            .then(() => {
              notifyOk('Заказ размещён!', 'Сборщики уведомлены')
              setIsSent(true)

              setTimeout(() => {
                setRedirectToOrders(true)
              }, 1000)
            })
            .catch(makeErrorNotification('Не удалось разместить заказ'))
        }
        else {
          notifyOk('Заказ обновлён!', `Заказ #${order.id} успешно обновлён`)

          if (wantToPlaceOrder) {
            notifyWarning(
              'Не удалось опубликовать заказ',
              'Заказ уже опубликован!'
            )
          }

          setRedirectToOrders(true)
        }
      }
    },
    [ isOrderPlaceable, order, orderSettings.forcePlaceOrder, placeOrder ]
  )

  // refetch order info after shop changed
  const handleOrderShopChange = useCallback(() => {
    refetch()
  }, [ refetch ])

  if (loading) {
    return <AppLoader.RowsWithText content="Загружаем заказ" />
  }

  if (error) {
    return <GraphQLCommonErrorDisplay error={error} />
  }

  if (!order) {
    return <p>Order not found!</p>
  }

  if (loadingPlaceOrder) {
    return <AppLoader content="Размещаем заказ" />
  }

  return (
    <div className="EditOrderForm">
      <ProductSearchContextProvider
        shop_id={order.shop_id}
        // customer_id={order?.assignedCustomerId}
      >
        <CartContextProvider
          shop_id={order.shop_id}
          initialProducts={order?.cartCompatibleCustomerProducts ?? []}
        >
          {isSent && <AppLoader content="Заказ размещён! 🚀" size="md" />}
          {redirectToOrders && <Redirect to={ROUTE_ORDERS} />}

          <OrderForm
            onShopChange={handleOrderShopChange}
            order={order}
            onFinish={handleFinish}
            orderCanBePlaced={isOrderPlaceable}
          />

          <Divider />

          <Panel header="Инструменты 🛠" collapsible>
            <Message
              type="info"
              header="Утилиты для работы с заказами"
            ></Message>

            <div>
              <Whisper
                preventOverflow
                trigger="hover"
                placement="topStart"
                speaker={
                  <Tooltip>
                    По умолчанию если заказ уже размещён вы можете только
                    изменять данные. В случае изменения корзины требуется
                    переразмещать заказ заново
                  </Tooltip>
                }
              >
                <span>
                  <Checkbox
                    checked={orderSettings.forcePlaceOrder}
                    onChange={(_, forcePlaceOrder: boolean) =>
                      setOrderSettings((settings) =>
                        mergeLeft(settings, { forcePlaceOrder })
                      )
                    }
                  >
                    <strong>Переразместить заказ</strong>
                  </Checkbox>
                </span>
              </Whisper>
            </div>
          </Panel>
        </CartContextProvider>
      </ProductSearchContextProvider>
    </div>
  )
}
